// =============================================================================
// AU VISA DATA — the additive ingestion SOCKET (empty until lit).
//
// These three tables are where the DARK live-ingestion layer
// (apps/api/lib/eligibility/providers/au/ingest.ts) WRITES, and where a future
// live VisaDataSource READS. They ship EMPTY: the transcribed static pack
// (providers/au/data.ts) is the active source today. Nothing reads these until the
// live parsers are lit and the AU provider is registered with the live source.
//
// Conventions mirror platform_algorithm.ts: uuid id() + timestamps house style
// (_shared.ts), jsonb for structured signal, varchar source_url, an index on the
// query keys, and a UNIQUE on the natural dedup key so re-ingest upserts in place.
//
// ADDITIVE ONLY (rule #11): CREATE TABLE IF NOT EXISTS via the Neon tool, mirrored
// here in Drizzle and exported from index.ts. Nothing dropped or renamed. The DDL
// is OUTPUT in the build report — it is NOT applied by this task.
// =============================================================================

import {
  boolean,
  date,
  index,
  jsonb,
  pgTable,
  timestamp,
  uniqueIndex,
  varchar,
} from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

/** CSOL occupations (and any other AU occupation list). Keyed (anzsco_code,
 *  list_name) so the same code can sit on multiple lists and a re-ingest upserts.
 *  Holds the position-specific caveat where the list defines one. */
export const csolOccupations = pgTable(
  'csol_occupations',
  {
    id: id(),
    /** 6-digit ANZSCO code — the duties-assessed key, not the job title. */
    anzscoCode: varchar('anzsco_code', { length: 12 }).notNull(),
    /** Occupation name as published on the list. */
    name: varchar('name', { length: 200 }).notNull(),
    /** Which list this row belongs to — 'CSOL', 'MLTSSL/ROL', etc. */
    listName: varchar('list_name', { length: 40 }).notNull(),
    /** Classification system; defaults to ANZSCO for AU. */
    classificationSystem: varchar('classification_system', { length: 16 })
      .notNull()
      .default('ANZSCO'),
    /** Applicable Circumstances List item number (LIN 24/089 s7(2)), or null. */
    caveatItemNo: varchar('caveat_item_no', { length: 8 }),
    /** The caveat text, where the list attaches one to this position. */
    caveatText: varchar('caveat_text', { length: 600 }),
    /** When this list snapshot took effect (the list is a dated snapshot). */
    effectiveFrom: date('effective_from'),
    /** When it stops applying; null = current. */
    effectiveTo: date('effective_to'),
    /** The instrument this entry traces to (e.g. 'LIN 24/089 / F2024L01620'). */
    sourceInstrument: varchar('source_instrument', { length: 200 }),
    /** Canonical URL for the instrument. */
    sourceUrl: varchar('source_url', { length: 500 }),
    /** ISO date a human last verified this entry against the source. */
    lastVerified: date('last_verified'),
    ...timestamps,
  },
  (t) => ({
    // UNIQUE (anzsco_code, list_name) — a code sits on a list once, so re-ingest
    // upserts in place (ON CONFLICT) instead of duplicating.
    keyIdx: uniqueIndex('csol_occupations_key_uq').on(t.anzscoCode, t.listName),
    listIdx: index('csol_occupations_list_idx').on(t.listName),
  }),
);

/** Visa salary thresholds (CSIT, SSIT, FWHIT, …). Time-versioned so the 1-July
 *  reindex is a new row, not an overwrite. Keyed (key, effective_from). */
export const visaSalaryThresholds = pgTable(
  'visa_salary_thresholds',
  {
    id: id(),
    /** Threshold key — 'CSIT' | 'SSIT' | 'FWHIT' | …. */
    key: varchar('key', { length: 24 }).notNull(),
    /** The amount (stored as text to preserve exact published figures). */
    amount: varchar('amount', { length: 24 }).notNull(),
    /** ISO-4217 currency for the amount. */
    currency: varchar('currency', { length: 3 }).notNull().default('AUD'),
    /** When this figure takes effect (lodgement-date-aware downstream). */
    effectiveFrom: date('effective_from').notNull(),
    /** When it stops applying; null = current. */
    effectiveTo: date('effective_to'),
    /** The indexation instrument this figure traces to. */
    sourceInstrument: varchar('source_instrument', { length: 200 }),
    /** Canonical URL. */
    sourceUrl: varchar('source_url', { length: 500 }),
    /** 'authoritative' | 'secondary' | 'inferred' — the reindexed-but-not-yet-
     *  formal figures land as 'secondary' until the formal instrument. */
    confidence: varchar('confidence', { length: 16 }).notNull().default('authoritative'),
    /** ISO date a human last verified this figure. */
    lastVerified: date('last_verified'),
    ...timestamps,
  },
  (t) => ({
    keyIdx: index('visa_salary_thresholds_key_idx').on(t.key, t.effectiveFrom),
    // ADDITIVE (rule #11): the natural dedup key. Idempotent ingest upserts ON
    // CONFLICT (key, effective_from); without this UNIQUE every cron firing would
    // silently duplicate the time-versioned rows. Apply via the Neon tool:
    //   CREATE UNIQUE INDEX IF NOT EXISTS visa_salary_thresholds_key_uq
    //     ON visa_salary_thresholds (key, effective_from);
    // (the plain index above is kept — nothing dropped/renamed).
    keyUq: uniqueIndex('visa_salary_thresholds_key_uq').on(t.key, t.effectiveFrom),
  }),
);

/** Per-company sponsor signals (sanctioned / approved / accredited). Keyed by a
 *  normalized company_key. ABSENCE of a row is 'unknown', NEVER an asserted
 *  negative — the asymmetry invariant the sponsorship layer enforces. */
export const visaSponsorSignals = pgTable(
  'visa_sponsor_signals',
  {
    id: id(),
    /** Normalized company key (the dedup + join key). */
    companyKey: varchar('company_key', { length: 200 }).notNull(),
    /** Australian Business Number, where resolved. */
    abn: varchar('abn', { length: 16 }),
    /** Accredited-sponsor flag, where a snapshot asserts it; null = unknown. */
    accredited: boolean('accredited'),
    /** The structured signal bag (match score, source kind, occupations, …). */
    signal: jsonb('signal').$type<Record<string, unknown>>(),
    /** Canonical URL for the source feed. */
    sourceUrl: varchar('source_url', { length: 500 }),
    /** When the source snapshot was current (signals decay with snapshot age). */
    sourceEffectiveDate: date('source_effective_date'),
    /** ISO timestamp a human/feed last verified this signal. */
    lastVerified: timestamp('last_verified', { withTimezone: true }),
    ...timestamps,
  },
  (t) => ({
    companyIdx: index('visa_sponsor_signals_company_idx').on(t.companyKey),
    abnIdx: index('visa_sponsor_signals_abn_idx').on(t.abn),
  }),
);
